/**
 * HDMX Constants for Order related data and UI
 */

import { EXPIRY } from '@/data/constants/payment';
import { CreditCardFormDataType } from '@/data/Content/Payment';

/**
 * The default Quantity Unit, as so called unitless unit, opposite to
 * kilogram, meter, liter, pound, foot, quart
 * See [QuantityUnit](https://help.hcltechsw.com/commerce/9.0.0/database/database/qtyunit.html)
 */
export const UNITLESS_UNIT_ONE = 'C62';
export const ORDER_CONFIGS = {
	calculationUsage: '-1,-5,-6,-7',
	calculationUsageAll: '-1,-2,-3,-4,-5,-6,-7',
	calculateOrder: '1',
	realtimeInventory: 'true',
	inventoryValidation: true,
	inventoryValidationForFulfillmentCenter: true,
	allocate: '***',
	backOrder: '***',
	remerge: '***',
	check: '*n',
	x_deliverType: 'A2C',
};

/**
 * Logical source for order update payloads.
 * - CART: used when the flow starts from cart interactions.
 * - CHECKOUT: used for checkout step interactions.
 */
export const ORDER_FLOW_SOURCE = {
	CART: 'cart',
	CHECKOUT: 'checkout',
} as const;

/**
 * Allowed values for x_pageLoad in order/shipping payloads.
 * Keep these centralized to avoid hardcoded literals across flows.
 */
export const ORDER_PAGE_LOAD = {
	LOAD: 'LOAD',
	ALL: 'all',
} as const;

/**
 * Special order id token used by shipping_info endpoint for current cart context.
 */
export const ORDER_ID_SELF = '.';

export const SHIP_MODE_CODE_PICKUP = 'PickupInStore';
export const DELIVERY_TYPE_TRANSFER_CART = 'TRANSFER_CART';
export const PART_NUMBER_MAX_LENGTH = 70;
export const QUICK_ORDER_INITIAL_VALUES = 4;
export const QUICK_ORDER_MAX_VALUES = 10;
export const ORDER_STATUS = {
	BackOrdered: 'B',
	Submitted: 'I',
	Approved: 'M',
	NoInventory: 'L',
	ApprovalDenied: 'N',
	PendingOrder: 'P',
	Released: 'R',
	Shipped: 'S',
	PendingApproval: 'W',
	Canceled: 'X',
	Closed: 'D',
	LockedByReturn: 'RTN',
	LockedByAppeasement: 'APP',
};

export const ORDER_HISTORY_REVALIDATION_INTERVAL = 45000;
export const ORDER_TYPE = {
	RECURRING: 'REC',
};

export const QUANTITY_FIELD_MAX_LENGTH = 12;

export enum OrderShipType {
	BOPIS = '1',
	HOME_DELIVERY = '2',
}

export const SHIP_MODE_CODE = {
	PICK_UP_IN_STORE: 'PickupInStore',
	MAIL: 'Mail',
};

export enum HomeDeliveryOrderStatus {
	// Step 1
	G = 'G', // Recibida: Hemos recibido tu orden
	M = 'M', // Recibida: Hemos recibido tu orden
	A = 'A', // Pendiente: Asegura tus productos, finaliza tu compra

	// Step 2
	R = 'R', // En preparación: Estamos validando el inventario de tu orden

	// Step 3
	S = 'S', // En proceso: Estamos programando el envío de tu orden
	SE = 'SE', // En proceso: Estamos programando el envío de tu orden
	ER = 'ER', // En proceso: Estamos programando el envío de tu orden

	// Step 4
	T = 'T', // En camino: Tu orden está en camino

	// Step 5
	D = 'D', // Entregada: Hemos entregado tu orden

	// Defined but not used in react stepper
	P = 'P', // En transferencia: En transferencia
	X = 'X', // Cancelado: Cancelado
	PX = 'PX', // En proceso: En proceso
}

export enum BOPISOrderStatus {
	// Step 1
	G = 'G', // Recibida: Hemos recibido tu orden
	M = 'M', // Recibida: Hemos recibido tu orden
	A = 'A', // Pendiente: Asegura tus productos, finaliza tu compra

	// Step 2
	R = 'R', // En preparación: Estamos validando el inventario de tu orden

	// Step 3
	S = 'S', // Lista para recoger: Ya puedes recoger tu orden
	SE = 'SE', // Lista para recoger: Ya puedes recoger tu orden
	ER = 'ER', // Lista para recoger: Ya puedes recoger tu orden

	// Step 4
	D = 'D', // Entregada: Hemos entregado tu orden

	// Defined but not used in react stepper
	P = 'P', // En transferencia: En transferencia
	X = 'X', // Cancelado: Cancelado
	PX = 'PX', // En proceso: En proceso
}

export const ACCOUNT_CC = 'account';
export const CC_CVC = 'cc_cvc';
export const EXPIRE_MONTH = 'expire_month';
export const EXPIRE_YEAR = 'expire_year';
export const PO_NUMBER = 'poNumber';
export const PAYMENT = {
	policies: {
		COD: true,
		MasterCard: true,
		VISA: true,
		AMEX: true,
		oxxo: true,
		giftCard: true,
		openPay: true,
		paypal: true,
		creditAr: true,
	},
	paymentMethodName: {
		cod: 'PayInStore',
		mc: 'MasterCard',
		visa: 'VISA',
		amex: 'AMEX',
		carnet: 'CARNET',
		oxxo: 'Oxxo',
		giftCard: 'GiftCard',
		openPay: 'OpenPay',
		paypal: 'PayPal',
		master: 'Master Card',
		creditAr: 'CreditAR',
	},
} as const;

export const NON_CREDIT_CARD_PAYMENT_METHODS = [
	PAYMENT.paymentMethodName.cod,
	PAYMENT.paymentMethodName.oxxo,
	PAYMENT.paymentMethodName.openPay,
	PAYMENT.paymentMethodName.paypal,
] as const;

export const BLOCKED = [
	PAYMENT.paymentMethodName.visa,
	PAYMENT.paymentMethodName.giftCard,
] as const;

export const ACCOUNT = 'ACCOUNT';
export const LOCALE = 'locale';
export const PAYMENT_CONFIGS = {
	maxNumPayment: 3,
};
export const EMPTY_STRING = '';
export const STRING_TRUE = 'true';
export const STRING_FALSE = 'false;';
export const HYPHEN = '-';
export const IS_PERSONAL_ADDRESS_ALLOWED = 'x_isPersonalAddressesAllowedForShipping';
export const CREDIT_CARD_FORM_DATA_INIT: CreditCardFormDataType = {
	account: EMPTY_STRING,
	expire_month: EXPIRY.MONTHS[0],
	expire_year: EXPIRY.YEARS[0],
	cc_cvc: EMPTY_STRING,
	cc_brand: EMPTY_STRING,
};
export const INVENTORY_STATUS = {
	available: 'Available',
	allocated: 'Allocated',
	backordered: 'Backordered',
	unallocated: 'Unallocated',
};
export const CARD_PAYMENT_METHODS: string[] = [
	PAYMENT.paymentMethodName.amex,
	PAYMENT.paymentMethodName.mc,
	PAYMENT.paymentMethodName.visa,
];

export const CASH_PAYMENT_METHODS = [
	PAYMENT.paymentMethodName.giftCard,
	PAYMENT.paymentMethodName.cod,
	PAYMENT.paymentMethodName.oxxo,
	PAYMENT.paymentMethodName.openPay,
	PAYMENT.paymentMethodName.paypal,
	PAYMENT.paymentMethodName.creditAr,
];
export const PROPS_TO_MAP_FOR_CATENTRY = [
	'name',
	'thumbnail',
	'attributes',
	'seo',
	'parentCatalogGroupID',
	'manufacturer',
	'merchandisingAssociations',
	'shortDescription',
];
export const PO_NUMBER_STORAGE_PREFIX = ACCOUNT + HYPHEN + PO_NUMBER + HYPHEN;

export const DeliveryType = {
	delivery: 'Mail',
	bopis: 'PickupInStore',
	split: 'split',
};

export const DeliveryFields = {
	bopisShipType: 'PICK',
	deliveryShipType: 'SHP_CART',
	x_realtimeInventory: 'true',
	x_bopis: 'PICK',
	x_d: 'SHP',
	x_split: '',
	x_page: 'cart',
	x_pageLoad: 'all',
	x_deliverType: 'SHP_CART',
	error_fleet: '_ERR_FLEET_DOWN',
	generic_error: '_GENERIC_EXCEPTION',
	zipcode_no_available: 'Codigo Postal sin cobertura para entrega a domicilio.',
	som_service_not_available: 'CWXCO2000E',
	error_server: 'ERR_INTERNAL_SERVER_ERROR',
	x_pageCartItemUpdate: 'cart',
	x_pageLoadCartItemUpdate: 'itemupdate',
	x_transfer: 'TRANSFER_CART',
	installService: 'Install',
};

export const CART_UPDATE_TYPE = {
	item: 'item',
	all: 'all',
};

/**
 * Sales channel ids (BUSCHN_ID) sent as x_channelId when adding a payment
 * instruction, so the order is attributed to the channel that completed
 * checkout rather than the one that started the cart.
 */
export const CHANNEL_ID_WEB = '-1';
export const CHANNEL_ID_HTTP_DEVICE = '-6';
export const CHANNEL_ID_APP = '-7';
